/* eslint-disable import/no-internal-modules */
import { FunctionalComponent, Component } from 'preact'
import Header from '@insided/deprecated-control/header'
import Button from '@insided/control/uikit/Button'
import CustomPagesList, { CustomPage } from '@insided/control/modules/CustomPages/CustomPagesList'
import Message, { MessageTypes } from '@insided/deprecated-preact-components/design-system-components/message'
import EmptyPage from '@insided/deprecated-preact-components/empty-page/EmptyPage'
import DeleteConfirmationModal from '@insided/control/modules/CustomPages/DeleteConfirmationModal'
import { Message as BannerMessage } from '@insided/deprecated-control/ui-components/Message'
import axios from 'axios'


interface CustomPagesPlatformProps {
  customPages: CustomPage[] | []
  isSuccess: boolean
  defaultCdn: string
}

interface StatusMessage {
  messageType: MessageTypes | undefined
  message: null | string
}

interface CustomPagesPlatformState extends StatusMessage {
  isModalOpen: boolean
  isLoading: boolean
  error: string
  customPageToDelete?: CustomPage
  customPages: CustomPage[] | []
}

interface StatusNotificationProps extends StatusMessage {
  onDismissHandler: () => void
}

const StatusNotification: FunctionalComponent<StatusNotificationProps> = ({
  messageType,
  message,
  onDismissHandler
}) => (
  <Message isControl variant={messageType} onDismiss={onDismissHandler} timer={3000}>
    {message}
  </Message>
)

const NewPageButton: FunctionalComponent = () => (
  <a href='/platform/custom-pages/new'>
    <Button variant='primary' size='medium'>
      Add page
    </Button>
  </a>
)

export class CustomPagesPlatform extends Component<CustomPagesPlatformProps, CustomPagesPlatformState> {
  constructor(props: CustomPagesPlatformProps) {
    super(props)
    this.state = {
      messageType: undefined,
      message: null,
      isModalOpen: false,
      isLoading: false,
      error: '',
      customPages: props.customPages,
      customPageToDelete: undefined
    }

    this.triggerCustomPageDeleteModal = this.triggerCustomPageDeleteModal.bind(this)
    this.deleteCustomPage = this.deleteCustomPage.bind(this)
  }

  componentDidMount() {
    if (this.props.isSuccess) {
      this.setState({ messageType: 'success', message: 'New custom page was added successfully!' })
    } else {
      this.setState({ messageType: undefined, message: '', error: '' })
    }
  }

  triggerCustomPageDeleteModal(customPage: CustomPage) {
    this.setState({ isModalOpen: true, customPageToDelete: customPage })
  }

  async deleteCustomPage(customPage?: CustomPage) {
    if (!customPage) return

    const { id, title } = customPage

    this.setState({ isLoading: true, messageType: undefined, message: '' })

    try {
      const response = await axios.delete(`/platform/custom-pages/${id}`, {
        validateStatus: (state: number) => (state >= 200 && state < 300) || state === 422
      })

      this.setState({ isLoading: false })

      if (response.status === 422) {
        this.setState({ error: response.data.error })
      } else {
        const updatedCustomPages = this.state.customPages.filter((customPage) => customPage.id !== id)
        this.setState({
          messageType: 'success',
          message: `${title} page was successfully deleted!`,
          isModalOpen: false,
          customPages: updatedCustomPages
        })
      }
    } catch (e) {
      this.setState({ error: 'Something went wrong', isLoading: false })
    }
  }

  render() {
    const { customPages, message, messageType, isModalOpen, error, isLoading, customPageToDelete } = this.state
    const { defaultCdn } = this.props
    return (
      <div className='custom-pages-platform-page'>
        {messageType && (
          <StatusNotification
            messageType={messageType}
            message={message}
            onDismissHandler={() => this.setState({ messageType: undefined })}
          />
        )}
        <Header
          title='Custom Pages'
          disableSaveButton
          disableCancelButton
          customButtons={[<NewPageButton key='newPage' />]}
        />
        {!customPages.length ? (
          <EmptyPage
            img_src={`${defaultCdn}/images/empty_page.png`}
            header='You do not have any custom pages yet'
            text='Start now by adding the first custom page.'
            button={{ link: '/platform/custom-pages/new', text: 'Add page' }}
          />
        ) : (
          <div>
            <BannerMessage variant='success'>
              <ul className="custom-page-platform-page_banner-message-list">
                <li>Custom pages can be used for several use cases. Check out some inspiring examples <a target="_blank" rel="noreferrer" href="https://community.insided.com/product-updates/introducing-custom-pages-add-unique-customizable-pages-to-the-customer-hub-5626">here</a>.</li>
                <li>To get started, read <a target="_blank" rel="noreferrer" href="https://community.insided.com/guides-tutorials-and-learning-material-113/getting-started-with-custom-pages-5627">Getting started with custom pages</a>.</li>
              </ul>
            </BannerMessage>
            <CustomPagesList customPages={customPages} onCustomPageDelete={this.triggerCustomPageDeleteModal} />
          </div>
        )}
        {isModalOpen && (
          <DeleteConfirmationModal
            isOpen={isModalOpen}
            onClose={() => this.setState({ isModalOpen: false })}
            customPageTitle={customPageToDelete?.title}
            isLoading={isLoading}
            error={error}
            onDelete={async () => await this.deleteCustomPage(customPageToDelete)}
          />
        )}
      </div>
    )
  }
}

export default CustomPagesPlatform
